import * as React from "react";
import { useState } from "react";
import {
  Button,
  CircularProgress,
  Grid,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CreateNewFolderRoundedIcon from "@mui/icons-material/CreateNewFolderRounded";
import StarRateRoundedIcon from "@mui/icons-material/StarRateRounded";
import { useActividadesContext } from "../../Hook/useActividades";
import { useAuthContext } from "../../../../hooks/Auth";
import { calificarResultado } from "../../../../controllers/operaciones";
import { ModalRating } from "../../../Modal/ModalRating";
import OpenMarkdownPreview from "../../../../utils/openMarkdownPreview";

type ProjectActionsButtonsProps = {
  onSaveProject?: () => Promise<void> | void;
  data: string;
  resultadoPropuestaId?: string;
};

export const ProjectActionsButtons: React.FC<ProjectActionsButtonsProps> = ({
  onSaveProject,
  data,
  resultadoPropuestaId,
}) => {
  const { authMode } = useAuthContext();
  const {
    cargando,
    editMode,
    handleSaveEdit,
    handleEditToggle,
    handleUploadToSchoology,
  } = useActividadesContext();

  // --- Menu Exportar/Compartir ---
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [respuestaImg, setRespuestaImg] = useState<string>("");
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) =>
    setAnchorEl(event.currentTarget);

  const handleClose = () => setAnchorEl(null);

  const handleExportPDF = () => {
    handleClose();
    OpenMarkdownPreview({ data, respuestaImg });
  };

  // --- Rating Modal State ---
  const [modalOpen, setModalOpen] = useState(false);
  const [rating, setRating] = useState<number>(0);
  const [hasRated, setHasRated] = useState<boolean>(false);
  const [pendingSave, setPendingSave] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  const handleOpenModal = () => setModalOpen(true);

  // Copiar al portapapeles
  const copiarAlPortapapeles = () => {
    navigator.clipboard
      .writeText(data)
      .then(() => console.log("Texto copiado al portapapeles"))
      .catch((err) => {
        console.error("Error al copiar texto: ", err);
      });
  };

  const handleRequestCloseModal = (
    _ev?: unknown,
    _reason?: "backdropClick" | "escapeKeyDown" | "closeIcon"
  ) => {
    if (!hasRated && !saving) return;
    setModalOpen(false);
  };

  const handleRating = (n: number) => setRating(n);

  const handleGuardarCalificacion = async () => {
    if (!resultadoPropuestaId) {
      console.warn(
        "ProjectActionsButtons: resultadoPropuestaId no definido, no se puede calificar."
      );
      return;
    }
    if ((rating ?? 0) <= 0) return;

    setSaving(true);
    try {
      await calificarResultado(resultadoPropuestaId, rating);
      setHasRated(true);
    } finally {
      setSaving(false);
    }

    setModalOpen(false);

    if (pendingSave) {
      setPendingSave(false);
      await handleSaveEdit();
    }
  };

  const handleGuardarClick = async () => {
    if (saving || cargando) return;

    if (onSaveProject) {
      await onSaveProject();
      return;
    }

    await handleSaveEdit();
  };

  return (
    <Grid
      item
      xs={12}
      sm={8}
      md={6}
      sx={{
        display: "flex",
        flexDirection: "row",
        position: "fixed",
        bottom: 0,
        zIndex: 1000,
        left: "50%",
        transform: "translateX(-50%)",
        borderRadius: 100,
        justifyContent: "center",
        alignItems: "center",
        mb: 3,
        border: "1px solid #336B87",
        backgroundColor: "#1D3D4D",
      }}
    >
      {/* Modal de Calificación */}
      <ModalRating
        modalOpen={modalOpen}
        handleRating={handleRating}
        handleGuardarCalificacion={handleGuardarCalificacion}
        rating={rating}
        onRequestClose={handleRequestCloseModal}
        requireRating
        saving={saving}
      />

      {/* Copiar */}
      <Tooltip
        title="Copiar"
        arrow
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: "#1D3D4D",
              color: "#FFF",
              fontFamily: "Montserrat",
              fontSize: "0.875rem",
              padding: "8px 12px",
              borderRadius: "8px",
            },
          },
          arrow: { sx: { color: "#1D3D4D" } },
        }}
      >
        <Button
          disabled={cargando}
          onClick={copiarAlPortapapeles}
          variant="text"
          sx={{ borderRadius: 5, border: "0px solid #336B87", color: "#FFF" }}
        >
          <ContentCopyIcon sx={{ fontSize: 25 }} />
        </Button>
      </Tooltip>

      {/* Exportar / Compartir */}
      <>
        <Tooltip
          title="Exportar / Compartir"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: "#1D3D4D",
                color: "#FFF",
                fontFamily: "Montserrat",
                fontSize: "0.875rem",
                padding: "8px 12px",
                borderRadius: "8px",
              },
            },
            arrow: { sx: { color: "#1D3D4D" } },
          }}
        >
          <Button
            variant="text"
            disabled={cargando}
            onClick={handleClick}
            sx={{ borderRadius: 5, border: "0px solid #336B87", color: "#FFF" }}
          >
            <CreateNewFolderRoundedIcon sx={{ fontSize: 25 }} />
          </Button>
        </Tooltip>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          transformOrigin={{ vertical: "bottom", horizontal: "center" }}
          sx={{ fontFamily: "Montserrat" }}
          PaperProps={{
            sx: {
              bgcolor: "#1D3D4D",
              borderRadius: 5,
              fontFamily: "Montserrat",
              color: "#FFF",
            },
          }}
        >
          <MenuItem
            onClick={handleExportPDF}
            sx={{
              fontFamily: "Montserrat",
              transition:
                "background-color .15s ease, color .15s ease, padding-left .15s ease",
              "&:hover": {
                bgcolor: "#25485A",
                color: "#F8AD0C",
                pl: 2.5,
              },
              "&.Mui-focusVisible": {
                bgcolor: "#25485A",
                color: "#F8AD0C",
              },
              position: "relative",
              "&:hover::before, &.Mui-focusVisible::before": {
                content: '""',
                position: "absolute",
                left: 0,
                top: 6,
                bottom: 6,
                width: 4,
                borderRadius: 2,
                backgroundColor: "#F8AD0C",
              },
            }}
          >
            Exportar PDF
          </MenuItem>

          <MenuItem
            disabled={authMode !== "LTI"}
            sx={{ bgcolor: authMode !== "LTI" ? "#7a7a7a75" : undefined }}
            onClick={handleUploadToSchoology}
          >
            Compartir con Schoology
          </MenuItem>
        </Menu>
      </>

      {/* Calificar */}
      <Tooltip
        title="Calificar proyecto"
        arrow
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: "#1D3D4D",
              color: "#FFF",
              fontFamily: "Montserrat",
              fontSize: "0.875rem",
              padding: "8px 12px",
              borderRadius: "8px",
            },
          },
          arrow: { sx: { color: "#1D3D4D" } },
        }}
      >
        <Button
          disabled={cargando || saving || !resultadoPropuestaId}
          onClick={handleOpenModal}
          variant="text"
          sx={{
            borderRadius: 5,
            border: "0px solid #336B87",
            color: "#FFF",
          }}
        >
          <StarRateRoundedIcon sx={{ fontSize: 25 }} />
        </Button>
      </Tooltip>

      {/* Guardar / Editar */}
      {!editMode ? (
        <Tooltip
          title="Guardar"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: "#1D3D4D",
                color: "#FFF",
                fontFamily: "Montserrat",
                fontSize: "0.875rem",
                padding: "8px 12px",
                borderRadius: "8px",
              },
            },
            arrow: { sx: { color: "#1D3D4D" } },
          }}
        >
          <Button
            disabled={cargando || saving}
            onClick={handleGuardarClick}
            variant="text"
            sx={{
              borderRadius: 5,
              border: "0px solid #336B87",
              color: "#FFF",
            }}
          >
            {saving ? (
              <CircularProgress size={16} sx={{ color: "#336B87" }} />
            ) : (
              <SaveIcon sx={{ fontSize: 25 }} />
            )}
          </Button>
        </Tooltip>
      ) : (
        <Tooltip
          title="Editar"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: "#1D3D4D",
                color: "#FFF",
                fontFamily: "Montserrat",
                fontSize: "0.875rem",
                padding: "8px 12px",
                borderRadius: "8px",
              },
            },
            arrow: { sx: { color: "#1D3D4D" } },
          }}
        >
          <Button
            disabled={cargando || saving}
            onClick={handleEditToggle}
            variant="text"
            sx={{
              borderRadius: 5,
              border: "0px solid #336B87",
              color: "#FFF",
            }}
          >
            <EditIcon sx={{ fontSize: 25 }} />
          </Button>
        </Tooltip>
      )}
    </Grid>
  );
};
